/**
 * RetroactiveOverlay — Full-screen modal for missed clock-in/out detection.
 *
 * Shown when the app detects via geofence sessions that the member:
 *   - Was at a station but didn't clock in (missed_clock_in)
 *   - Left a station but is still clocked in (missed_clock_out)
 *
 * Provides time adjustment (+/- 15 min) and category picker for clock-in.
 * Follows ConfirmDialog visual pattern (dark backdrop, centered card).
 */

import { useState, useCallback } from 'react';
import { CATEGORIES } from '@shared/categories';
import type { CategoryId } from '@shared/categories';
import type { RetroactiveData } from '../hooks/useRetroactiveCheck';

interface RetroactiveOverlayProps {
  data: RetroactiveData;
  suggestionId: number;
  onAccept: (suggestionId: number, extra?: Record<string, string>, clockTime?: string) => Promise<boolean>;
  onDismiss: (suggestionId: number) => Promise<boolean>;
}

/** Time adjustment step in minutes */
const TIME_STEP_MIN = 15;

function formatTimeET(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  });
}

function adjustTime(iso: string, deltaMinutes: number): string {
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() + deltaMinutes);
  return d.toISOString();
}

export default function RetroactiveOverlay({ data, suggestionId, onAccept, onDismiss }: RetroactiveOverlayProps) {
  const isClockIn = data.type === 'missed_clock_in';
  const [adjustedTime, setAdjustedTime] = useState(data.suggested_time);
  const [category, setCategory] = useState<CategoryId>('operational');
  const [submitting, setSubmitting] = useState(false);

  const stationLabel = data.station_name ?? 'the station';
  const timeLabel = formatTimeET(adjustedTime);

  const handleEarlier = useCallback(() => {
    setAdjustedTime((t) => adjustTime(t, -TIME_STEP_MIN));
  }, []);

  const handleLater = useCallback(() => {
    setAdjustedTime((t) => adjustTime(t, TIME_STEP_MIN));
  }, []);

  const handleAccept = useCallback(async () => {
    setSubmitting(true);
    const extra: Record<string, string> = {};
    if (isClockIn) {
      extra.category = category;
    }
    const success = await onAccept(suggestionId, extra, adjustedTime);
    if (!success) setSubmitting(false);
  }, [suggestionId, onAccept, adjustedTime, isClockIn, category]);

  const handleDismiss = useCallback(async () => {
    setSubmitting(true);
    const success = await onDismiss(suggestionId);
    if (!success) setSubmitting(false);
  }, [suggestionId, onDismiss]);

  const accentBorder = isClockIn ? 'rgba(34, 197, 94, 0.4)' : 'rgba(251, 191, 36, 0.4)';
  const accentBg = isClockIn ? 'rgba(34, 197, 94, 0.08)' : 'rgba(251, 191, 36, 0.08)';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={isClockIn ? 'Missed clock-in detected' : 'Missed clock-out detected'}
      className="fixed inset-0 flex items-center justify-center bg-black/70 z-[1001] p-6 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) handleDismiss();
      }}
    >
      <div className="w-full max-w-[380px] bg-surface-overlay rounded-2xl border border-edge-default p-6 shadow-xl">
        {/* Header with accent */}
        <div
          className="rounded-xl px-4 py-3 mb-5"
          style={{ background: accentBg, borderLeft: `3px solid ${accentBorder}` }}
        >
          <h2 className="text-base font-bold text-content-primary mb-1">
            {isClockIn ? 'Missed Clock-In?' : 'Still Clocked In'}
          </h2>
          <p className="text-[13px] text-content-secondary leading-snug">{data.message}</p>
        </div>

        {/* Suggested time with adjustment */}
        <div className="mb-5">
          <div className="text-[11px] font-semibold text-content-tertiary uppercase tracking-wider mb-2">
            {isClockIn ? 'Start time' : 'End time'}
          </div>
          <div className="flex items-center gap-2 justify-center">
            <button
              type="button"
              onClick={handleEarlier}
              disabled={submitting}
              className="w-10 h-10 rounded-lg border border-edge-default bg-surface-raised text-content-primary text-lg font-bold cursor-pointer hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="15 minutes earlier"
            >
              -
            </button>
            <div
              className="px-5 py-2.5 rounded-lg text-lg font-bold text-content-primary"
              style={{ background: accentBg, border: `1px solid ${accentBorder}` }}
            >
              {timeLabel}
            </div>
            <button
              type="button"
              onClick={handleLater}
              disabled={submitting}
              className="w-10 h-10 rounded-lg border border-edge-default bg-surface-raised text-content-primary text-lg font-bold cursor-pointer hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="15 minutes later"
            >
              +
            </button>
          </div>
          <div className="text-[11px] text-content-tertiary text-center mt-1.5">Adjust in 15-min increments</div>
        </div>

        {/* Category picker (clock-in only) */}
        {isClockIn && (
          <div className="mb-5">
            <div className="text-[11px] font-semibold text-content-tertiary uppercase tracking-wider mb-2">
              Category
            </div>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategory(cat.id)}
                  disabled={submitting}
                  className={`py-2 px-3 text-sm font-semibold rounded-lg border cursor-pointer transition-colors
                    ${
                      category === cat.id
                        ? 'text-content-primary'
                        : 'border-edge-default bg-surface-raised text-content-secondary hover:bg-surface-sunken'
                    }
                    disabled:cursor-not-allowed disabled:opacity-50`}
                  style={category === cat.id ? { borderColor: accentBorder, background: accentBg } : undefined}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Station info */}
        <div className="flex justify-between items-center px-3.5 py-2.5 bg-surface-sunken rounded-lg mb-5">
          <span className="text-xs text-content-tertiary uppercase tracking-wider">Station</span>
          <span className="text-sm font-semibold text-content-primary">{stationLabel}</span>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleDismiss}
            disabled={submitting}
            className="flex-1 py-3.5 text-base font-semibold rounded-lg border border-edge-default
                       bg-transparent text-content-secondary cursor-pointer
                       hover:bg-surface-sunken transition-colors duration-150
                       disabled:cursor-not-allowed disabled:opacity-50"
          >
            Not Now
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={submitting}
            className={`flex-1 py-3.5 text-base font-bold rounded-lg border-none cursor-pointer
                       transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60
                       ${isClockIn ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-amber-500 text-gray-900 hover:bg-amber-600'}`}
          >
            {submitting ? '...' : isClockIn ? `Clock In at ${timeLabel}` : `Clock Out at ${timeLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}
